import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { DesktopAppService } from './desktop-app.service';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';

/**
 * Tells the Electron main-process `TrackReminder` whether a timer is currently
 * running. Bridges the Angular timer state to the IPC API exposed by the
 * preload as `window.visarcDesktop.setTimerRunning`.
 *
 * Outside the Visarc Timer Electron shell `window.visarcDesktop` is undefined
 * and this service is a complete no-op — the website is unaffected.
 *
 * Eagerly instantiated by `AppComponent` so it's listening from boot.
 */
@Injectable({ providedIn: 'root' })
export class TrackReminderBridgeService implements OnDestroy {
    private subs: Subscription[] = [];
    private setTimerRunning: ((running: boolean) => Promise<void>) | null = null;

    constructor(
        timerEntry: TimerEntryService,
        desktopApp: DesktopAppService,
    ) {
        if (!desktopApp.isTimerApp) return;
        const api = typeof window !== 'undefined' ? window.visarcDesktop : undefined;
        if (!api?.setTimerRunning) return;
        // Bind once so we don't re-fetch the API on every event.
        this.setTimerRunning = api.setTimerRunning.bind(api);

        // Initial state: assume no timer running. The timer-header runs
        // `getRunningTimer()` on init and calls `addEntry()` if there is one,
        // which fires `newEntry$` and lands on the subscription below.
        this.setTimerRunning(false).catch(() => { /* no-op */ });

        this.subs.push(timerEntry.newEntry$.subscribe(() => {
            this.setTimerRunning?.(true).catch(() => { /* no-op */ });
        }));
        this.subs.push(timerEntry.stopEntry$.subscribe(() => {
            this.setTimerRunning?.(false).catch(() => { /* no-op */ });
        }));
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
        this.subs = [];
    }
}
